import { useNavigate } from "react-router-dom";
import { useCollection } from "react-firebase-hooks/firestore";
import { collection, orderBy, query, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Button } from "../../components/ui/button";
import { PlusCircle } from "lucide-react";
import BlogCard from "../../components/BlogCard";
import { Skeleton } from "../../components/ui/skeleton";

interface Blog {
  id: string;
  title: string;
  description: string;
  author: string;
  contentHtml: string;
  thumbnailUrl: string;
  createdAt: Timestamp;
}

const Blogs = () => {
  const navigate = useNavigate();
  const [blogs, loading, error] = useCollection(
    query(collection(db, "BlogsWeb"), orderBy("createdAt", "desc"))
  );

  const handleCreateNew = () => {
    navigate("/admin/blogs/create");
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/blogs/${id}/edit`);
  };

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-red-200 bg-red-50">
        <p className="text-sm text-red-500">
          Error loading blogs: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl text-gray-900">Blog Posts</h1>
        <Button
          onClick={handleCreateNew}
          className="bg-forest-green hover:bg-forest-green/90 text-white"
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          Create New Blog
        </Button>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="space-y-4 rounded-lg border p-4">
              <Skeleton className="h-48 w-full" /> {/* Thumbnail skeleton */}
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-10 w-full" />
              <div className="flex justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : blogs?.empty ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg border border-dashed">
          <h3 className="font-heading text-xl text-gray-900">No blogs yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Create your first blog post to get started
          </p>
          <Button
            onClick={handleCreateNew}
            variant="outline"
            className="mt-4 border-forest-green text-forest-green hover:bg-forest-green/5"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Create New Blog
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {blogs?.docs.map((doc) => {
            const blog = { id: doc.id, ...doc.data() } as Blog;
            return (
              <BlogCard
                key={doc.id}
                id={doc.id}
                title={blog.title}
                description={blog.description}
                author={blog.author}
                thumbnailUrl={blog.thumbnailUrl}
                createdAt={blog.createdAt}
                onEdit={() => handleEdit(doc.id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Blogs;
